import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { context } from 'bookingbug-core-js';
import { environment } from '../../environments/environment';

// HeyCentric pre-basket (book-after-payment) client — mirrors the Westminster
// appointments service. Nothing here creates a booking: the client persists its
// intent via /pre-basket, redirects to the HeyCentric hosted payment page, and
// bl-hey-centric checks the basket out server-side once the gateway confirms.
// The verify pages only poll /pre-basket/find until the row reaches PAID.
@Injectable({
  providedIn: "root",
})
export class HeyCentricService {

  // Environment-aware on purpose: the tenant key is derived server-side from the
  // JRNI host in the body, but the bl-hey-centric deployment itself differs per
  // environment. Hardcoding staging here is the known develop→main URL trap.
  private url = environment.production
    ? 'https://api.bookinglab.co.uk'
    : 'https://staging.bookinglab.co.uk';
  // <base href="/"> in index.html
  private path = "/";
  heyCentricUrlPath = "/payment/";

  constructor(private http: HttpClient) { }

  // Normalise a pre-basket failure into one shape the summary component can
  // branch on. The backend's pre-payment gate answers 409 with
  // [{err: "Payment blocked before charge: <reason>"}] — `blocked: true` means
  // the basket itself is unusable (empty / not on this session), so retrying
  // the same basket is futile and the caller should rebuild it instead.
  private toPaymentError(err: any, body: any) {
    if (err.requestBody) {
      return err;
    }
    const blocked = err.status === 409;
    const reason = (blocked && Array.isArray(err.error) && err.error[0] && err.error[0].err)
      ? err.error[0].err
      : undefined;
    return { status: err.status, message: reason || err.message, blocked, reason, requestBody: body };
  }

  private portSuffix(): string {
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    return ports.indexOf(window.location.port) === -1 ? `:${window.location.port}` : '';
  }

  // Builds the leading-comma service-item list and the return URL. Throws when a
  // service item has no id: an upstream basket/service-item call failed silently
  // (api.service returns error STRINGS) and we must never build
  // ".../payment-verify/<company>/<basket>/,undefined".
  private buildReturnUrl(caller: string, company, basket, serviceItems, journeyRoute): string {
    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      if (!serviceItem || serviceItem.id == null) {
        throw new Error(
          `HeyCentric ${caller} aborted: service item missing id ` +
          `(journeyRoute=${journeyRoute}, company=${company?.id}, basket=${basket?.id}, ` +
          `serviceItems=${JSON.stringify(serviceItems)})`
        );
      }
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    const origin = `${window.location.protocol}//${window.location.hostname}${this.portSuffix()}`;
    let returnUrl: string;
    if (journeyRoute == "order_a_copy_certificate") {
      returnUrl = `${origin}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    } else {
      returnUrl = `${origin}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    }
    console.log(`journeyRoute: ${journeyRoute} Return URL: ${returnUrl}`);
    return returnUrl;
  }

  // Array → amounts/rev_codes (multi-line, index-aligned with descriptions);
  // scalar → amount/rev_code.
  private applyAmountsAndRevCodes(body: any, amounts, revCode) {
    if (Array.isArray(amounts)) {
      body['amounts'] = amounts;
    }
    else {
      body['amount'] = `${amounts}`;
    }
    if (Array.isArray(revCode)) {
      body['rev_codes'] = revCode;
    }
    else {
      body['rev_code'] = `${revCode}`;
    }
  }

  private async post(endpoint: string, body: any) {
    try {
      const response: any = await this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/${endpoint}`, body).toPromise();
      if (!response?.url) {
        throw { status: 0, message: 'No redirect URL in response', requestBody: body };
      }
      return response;
    } catch (err: any) {
      throw this.toPaymentError(err, body);
    }
  }

  async preBasketInvoke(company, basket, serviceItems, descriptions, client, revCode, amounts, authToken, data, detailGroupId, slot, extraDuration, journeyRoute, parentCompanyId?) {
    const returnUrl = this.buildReturnUrl('preBasketInvoke', company, basket, serviceItems, journeyRoute);

    const body = {
      host: `${context.apiUrl}`,
      // company_id MUST be the PARENT (token-holding) company. child_company stays the location.
      company_id: parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      data: data,
      return_url: `${returnUrl}`,
      auth_token: `${authToken}`,
      detail_group: detailGroupId,
      slot: slot,
      extra_duration: extraDuration,
      journey_route: journeyRoute
    };
    this.applyAmountsAndRevCodes(body, amounts, revCode);

    return this.post('pre-basket', body);
  }

  // Replay a body a previous preBasketInvoke built (err.requestBody). Used when
  // the first attempt failed for a non-basket reason: re-running the whole
  // journey would mint a new auth token and can leave an empty basket behind.
  async preBasketInvokeRetry(body: any) {
    return this.post('pre-basket', body);
  }

  async preBasketFind(companyId, basketId, serviceItemId) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
    };
    return this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/find`, body).toPromise();
  }

  // Kept for the ARP / declined-payment stamps only. The live success path never
  // calls this: writing RETURNED_FROM_GATEWAY from the client can regress a row
  // the callback has already moved to PAID.
  async preBasketUpdate(companyId, basketId, serviceItemId, status, ref, data) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
      ref: ref,
      status: `${status}`,
      data: data
    };
    return this.http.post(`${this.url}${this.heyCentricUrlPath}hey-centric/pre-basket/update`, body).toPromise();
  }

  async preBasketTelephonyInvoke(company, basket, serviceItems, descriptions, client, revCode, amounts, authToken, data, detailGroupId, slot, extraDuration, bu, agentId, agentEmail, agentPhone, journeyRoute, parentCompanyId?) {
    const returnUrl = this.buildReturnUrl('preBasketTelephonyInvoke', company, basket, serviceItems, journeyRoute);

    const body = {
      host: `${context.apiUrl}`,
      // company_id MUST be the PARENT (token-holding) company. child_company stays the location.
      company_id: parentCompanyId === undefined ? `${company.id}` : parentCompanyId,
      child_company: `${company.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      data: data,
      return_url: `${returnUrl}`,
      auth_token: `${authToken}`,
      detail_group: detailGroupId,
      slot: slot,
      extra_duration: extraDuration,
      bu: `${bu}`,
      "agent_id": `${agentId}`,
      "agent_email": `${agentEmail}`,
      "agent_phone": `${agentPhone}`,
      "journey_route": `${journeyRoute}`
    };
    this.applyAmountsAndRevCodes(body, amounts, revCode);

    return this.post('pre-basket/arp', body);
  }

  async preBasketTelephonyInvokeRetry(body: any) {
    return this.post('pre-basket/arp', body);
  }
}
